import { useTranslation } from 'react-i18next'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { notification, Switch, Typography } from 'antd'
import useDirectory from 'hooks/useDirectory'
import { Building } from 'models/Building'
import { useRecoilValue } from 'recoil'
import {
  buildingModeState,
  selectedAreaState,
  selectedPoint,
  selectedSubImageState
} from 'store/buildingStore'
import Krpano from 'utils/krpano'
import { KrpanoEngine } from 'utils/KrpanoEngine'

import Link from 'components/atoms/Link'
import CopyUrlIcon from 'components/icons/CopyUrlIcon'
import PreviewIcon from 'components/icons/PreviewIcon'

import AlertCalendarNavIcon from '../../icons/alert-calendar-nav-icon'
import Sidebar from '../Sidebar'
import UserDropdown from '../UserDropdown'

type Props = {
  building?: Building
}

export default function HeaderDetailBuilding({ building }: Props) {
  const { t } = useTranslation()
  const { id, floorId } = useParams()
  const { pathname } = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const isAnnotationDisplay = searchParams.get('is_display')
  const { getDirectoryBasename, logoHeader } = useDirectory()

  const currentSelectedPoint = useRecoilValue(selectedPoint)
  const buildingMode = useRecoilValue(buildingModeState)
  const selectedArea = useRecoilValue(selectedAreaState)

  const sessionParams = sessionStorage.getItem('buildingParams')

  const localStoringFloor = () => {
    floorId && localStorage.setItem('floor', floorId)
    window.open(
      `${getDirectoryBasename()}/viewer/buildings/${id}?is_display=${
        isAnnotationDisplay || 'true'
      }`,
      '_blank'
    )
  }

  const selectedSubImage = useRecoilValue(selectedSubImageState)

  const handleCopyUrl = () => {
    let link = `${
      window.location.origin
    }${getDirectoryBasename()}/viewer/buildings/${id}`
    if (floorId) {
      link += `?floor=${floorId}`
    }
    if (selectedArea) {
      link += `&area=${selectedArea}`
    }
    if (currentSelectedPoint) {
      const viewer = new KrpanoEngine(
        buildingMode.previewMode ? 'full' : 'preview'
      )
      const { hlookat } = viewer
      const { vlookat } = viewer
      const { fov } = viewer
      link += `&point=${currentSelectedPoint}&hlookat=${hlookat || 0}&vlookat=${
        vlookat || 0
      }&fov=${fov || 80}&is_display=${isAnnotationDisplay || 'true'}`
      if (selectedSubImage) {
        link += `&subimage=${selectedSubImage}`
      }
    }
    navigator.clipboard.writeText(link)
    notification.success({
      key: 'copy-successful',
      message: t('translation.copyUrlSuccessful')
    })
  }

  const handleSwitchViewingAnnotation = (checked: boolean) => {
    searchParams.set('is_display', checked ? 'true' : 'false')
    setSearchParams(searchParams, {
      replace: true
    })
    const viewer = new KrpanoEngine('full')
    Krpano.ToggleDisplayHotspot(viewer.instance, checked)
  }

  return (
    <div className="h-full grid grid-cols-3 items-center pl-5 theme-detail-building-header">
      <div className="flex items-center gap-2 select-none">
        <Sidebar />

        <Link to={sessionParams ? `/buildings?${sessionParams}` : '/buildings'}>
          <img
            src={logoHeader}
            alt="logo"
            className="max-w-[207px] cursor-pointer ml-[53px]"
          />
        </Link>
      </div>
      <div>
        <Typography.Paragraph
          ellipsis={{
            rows: 1,
            tooltip: building?.organization?.name
          }}
          className="text-white text-center text-[14px] font-[500] truncate !mb-0"
        >
          {building?.organization?.name}
        </Typography.Paragraph>
        <Typography.Paragraph
          ellipsis={{
            rows: 1,
            tooltip: building?.name
          }}
          className="text-white text-center truncate font-[500] !mb-0"
        >
          {building?.name}
        </Typography.Paragraph>
      </div>
      <div
        className="relative h-full flex justify-end gap-6"
        style={{
          backgroundImage: "url('/assets/background_header.png')"
        }}
      >
        <div className="h-[35px] my-auto  place-items-center hidden">
          <div className="flex items-center justify-center w-6 h-6">
            <Switch
              checked={isAnnotationDisplay === 'true' || !isAnnotationDisplay}
              size="small"
              onChange={handleSwitchViewingAnnotation}
            />
          </div>
          <Typography className=" text-center text-[11px] leading-[11px] font-[700] text-white">
            アイコン
          </Typography>
        </div>

        <Link
          to={(() => {
            const params = new URLSearchParams()
            if (id) params.set('building_id', id)
            if (floorId) params.set('floor_id', floorId)
            const qs = params.toString()
            return `/alert-calendar${qs ? `?${qs}` : ''}`
          })()}
          id="detail-building-header-calendar-link"
          className="h-[35px] my-auto grid place-items-center cursor-pointer no-underline text-current hover:text-current"
        >
          <div className="flex items-center justify-center w-6 h-6">
            <AlertCalendarNavIcon
              style={{
                fontSize: 20,
                color: pathname.includes('alert-calendar')
                  ? 'var(--theme-sub-primary, #91DCE5)'
                  : 'white'
              }}
            />
          </div>
          <Typography
            className={`text-center text-[11px] leading-[11px] font-[700] ${
              pathname.includes('alert-calendar')
                ? 'theme-text-sub-primary'
                : 'text-white'
            }`}
          >
            {t('alert.calendar', 'アラート')}
          </Typography>
        </Link>
        <div
          className="h-[35px] my-auto grid place-items-center cursor-pointer"
          onClick={handleCopyUrl}
        >
          <div className="flex items-center justify-center w-6 h-6">
            <CopyUrlIcon />
          </div>
          <Typography className=" text-center text-[11px] leading-[11px] font-[700] text-white">
            {t('common.header.copyUrl')}
          </Typography>
        </div>
        <div
          className="h-[35px] my-auto grid place-items-center cursor-pointer"
          onClick={localStoringFloor}
        >
          <div className="flex items-center justify-center w-6 h-6">
            <PreviewIcon />
          </div>
          <Typography className=" text-center text-[11px] leading-[11px] font-[700] text-white">
            {t('common.header.preview')}
          </Typography>
        </div>

        <UserDropdown t={t} />
      </div>
    </div>
  )
}

HeaderDetailBuilding.defaultProps = {
  building: undefined
}
